import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Search, Trash2, ArrowLeft, Upload, X } from "lucide-react";
import VehiclePhoto from "@/components/app/VehiclePhoto";

function nzd(n) { return new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 }).format(n || 0); }

const EMPTY = {
  stock_no: "", vin: "", rego: "", year: new Date().getFullYear(), make: "", model: "",
  body_type: "", transmission: "Automatic", fuel_type: "Petrol", engine_size: "", color: "",
  odometer: 0, purchase_cost: 0, reconditioning_cost: 0, transport_cost: 0, list_price: 0,
  status: "available", photos: [], features: [], notes: "",
};

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const inputCls = "w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

export default function VehicleDetail() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [v, setV] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regoInput, setRegoInput] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/vehicles/${id}`);
        setV({ ...EMPTY, ...data });
        setRegoInput(data.rego || "");
      } catch { toast.error("Vehicle not found"); navigate("/vehicles"); }
      setLoading(false);
    })();
  }, [id, isNew, navigate]);

  const cost = useMemo(() => (+v.purchase_cost || 0) + (+v.reconditioning_cost || 0) + (+v.transport_cost || 0), [v]);
  const margin = (+v.list_price || 0) - cost;
  const marginPct = v.list_price > 0 ? (margin / v.list_price) * 100 : 0;

  const change = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const doLookup = async () => {
    if (!regoInput.trim()) return;
    try {
      const { data } = await api.get(`/lookup/rego/${regoInput.trim()}`);
      setV((s) => ({ ...s, ...data }));
      toast.success(`Vehicle details loaded from Rego lookup`);
    } catch { toast.error("Rego lookup failed"); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/vehicle-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setV((s) => ({ ...s, photos: [...(s.photos || []), data.path] }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error("Upload failed"); }
    setUploading(false);
  };

  const removePhoto = (path) => setV((s) => ({ ...s, photos: s.photos.filter((p) => p !== path) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...v, year: +v.year, odometer: +v.odometer || 0, purchase_cost: +v.purchase_cost || 0, reconditioning_cost: +v.reconditioning_cost || 0, transport_cost: +v.transport_cost || 0, list_price: +v.list_price || 0 };
      if (isNew) {
        const { data } = await api.post("/vehicles", payload);
        toast.success("Vehicle created");
        navigate(`/vehicles/${data.id}`);
      } else {
        await api.put(`/vehicles/${id}`, payload);
        toast.success("Vehicle saved");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const remove = async () => {
    if (!window.confirm("Delete this vehicle?")) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Deleted");
      navigate("/vehicles");
    } catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/vehicles")} className="p-2 rounded-md hover:bg-slate-200 text-slate-600"><ArrowLeft className="w-4 h-4" /></button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{isNew ? "Add Vehicle" : `${v.year} ${v.make} ${v.model}`}</h1>
          <p className="text-sm text-slate-500 mt-1 font-mono">{isNew ? "New stock entry" : `${v.stock_no} · ${v.rego}`}</p>
        </div>
        {!isNew && (
          <button onClick={remove} data-testid="vehicle-delete-button" className="h-10 px-3 border border-rose-200 text-rose-700 hover:bg-rose-50 rounded-lg text-sm font-semibold flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        )}
        <button onClick={save} disabled={saving} data-testid="vehicle-save-button" className="h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg flex items-center gap-2 disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card-elevated p-6">
            <h3 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>Rego / VIN Lookup</h3>
            <div className="flex gap-2">
              <input data-testid="rego-lookup-input" value={regoInput} onChange={(e) => setRegoInput(e.target.value.toUpperCase())} placeholder="e.g. KLP982" className={`${inputCls} font-mono`} />
              <button data-testid="rego-lookup-button" onClick={doLookup} className="h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg flex items-center gap-2 whitespace-nowrap">
                <Search className="w-4 h-4" /> Lookup
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">Auto-populates make, model, year, VIN and specs.</p>
          </div>

          <div className="card-elevated p-6 space-y-4">
            <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Vehicle details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Stock #"><input value={v.stock_no} onChange={(e) => change("stock_no", e.target.value)} className={`${inputCls} font-mono`} placeholder="Auto-generated if empty" /></Field>
              <Field label="Status">
                <select value={v.status} onChange={(e) => change("status", e.target.value)} className={inputCls}>
                  <option value="available">Available</option>
                  <option value="reserved">Reserved</option>
                  <option value="in_prep">In Prep</option>
                  <option value="sold">Sold</option>
                </select>
              </Field>
              <Field label="Rego"><input data-testid="vehicle-rego-input" value={v.rego} onChange={(e) => change("rego", e.target.value.toUpperCase())} className={`${inputCls} font-mono`} /></Field>
              <Field label="VIN"><input data-testid="vin-lookup-input" value={v.vin} onChange={(e) => change("vin", e.target.value.toUpperCase())} className={`${inputCls} font-mono`} /></Field>
              <Field label="Year"><input type="number" value={v.year} onChange={(e) => change("year", e.target.value)} className={inputCls} /></Field>
              <Field label="Odometer (km)"><input type="number" value={v.odometer} onChange={(e) => change("odometer", e.target.value)} className={inputCls} /></Field>
              <Field label="Make"><input value={v.make} onChange={(e) => change("make", e.target.value)} className={inputCls} /></Field>
              <Field label="Model"><input value={v.model} onChange={(e) => change("model", e.target.value)} className={inputCls} /></Field>
              <Field label="Body type"><input value={v.body_type} onChange={(e) => change("body_type", e.target.value)} className={inputCls} placeholder="SUV, Sedan, Ute…" /></Field>
              <Field label="Color"><input value={v.color} onChange={(e) => change("color", e.target.value)} className={inputCls} /></Field>
              <Field label="Transmission">
                <select value={v.transmission} onChange={(e) => change("transmission", e.target.value)} className={inputCls}>
                  <option>Automatic</option><option>Manual</option><option>CVT</option>
                </select>
              </Field>
              <Field label="Fuel type">
                <select value={v.fuel_type} onChange={(e) => change("fuel_type", e.target.value)} className={inputCls}>
                  <option>Petrol</option><option>Diesel</option><option>Hybrid</option><option>Electric</option>
                </select>
              </Field>
              <Field label="Engine size" className="sm:col-span-2"><input value={v.engine_size} onChange={(e) => change("engine_size", e.target.value)} className={inputCls} placeholder="2.5L" /></Field>
              <Field label="Notes" className="sm:col-span-2"><textarea value={v.notes} onChange={(e) => change("notes", e.target.value)} rows={3} className={`${inputCls} h-auto py-2`} /></Field>
            </div>
          </div>

          <div className="card-elevated p-6">
            <h3 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>Photos</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(v.photos || []).map((p) => (
                <div key={p} className="relative aspect-[4/3] bg-slate-100 rounded-lg overflow-hidden group">
                  <VehiclePhoto vehicle={{ photos: [p] }} className="w-full h-full object-cover" />
                  <button onClick={() => removePhoto(p)} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <label className="aspect-[4/3] border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-500 hover:border-blue-500 hover:text-blue-600 cursor-pointer transition">
                <Upload className="w-5 h-5" />
                <span className="text-xs font-semibold mt-1">{uploading ? "Uploading…" : "Upload"}</span>
                <input data-testid="photo-upload-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
              </label>
            </div>
          </div>
        </div>

        {/* Financials */}
        <div className="space-y-6">
          <div className="card-elevated p-6 space-y-4">
            <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>Financials</h3>
            <Field label="Purchase cost">
              <input data-testid="vehicle-cost-input" type="number" value={v.purchase_cost} onChange={(e) => change("purchase_cost", e.target.value)} className={`${inputCls} font-mono`} />
            </Field>
            <Field label="Reconditioning cost">
              <input type="number" value={v.reconditioning_cost} onChange={(e) => change("reconditioning_cost", e.target.value)} className={`${inputCls} font-mono`} />
            </Field>
            <Field label="Transport cost">
              <input type="number" value={v.transport_cost} onChange={(e) => change("transport_cost", e.target.value)} className={`${inputCls} font-mono`} />
            </Field>
            <div className="pt-3 border-t border-slate-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Total cost</span>
                <span className="font-mono font-bold text-slate-900">{nzd(cost)}</span>
              </div>
            </div>
            <Field label="List price">
              <input data-testid="vehicle-price-input" type="number" value={v.list_price} onChange={(e) => change("list_price", e.target.value)} className={`${inputCls} font-mono text-base font-semibold`} />
            </Field>
            <div data-testid="vehicle-margin-display" className={`rounded-lg p-4 ${margin >= 0 ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Gross Profit</div>
              <div className={`text-2xl font-bold mt-1 font-mono ${margin >= 0 ? "text-emerald-700" : "text-rose-700"}`} style={{ fontFamily: "JetBrains Mono" }}>{nzd(margin)}</div>
              <div className={`text-xs font-semibold mt-1 ${margin >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{marginPct.toFixed(1)}% margin</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
